// Query params that are credentials, not diagnostics. `guest_session_token` is a
// live JWT that authorises the bearer to read and mutate a guest's order, and it
// rides in the URL of every checkout page — so it was being written verbatim into
// Sentry event URLs, breadcrumbs and request data. Anyone with read access to the
// Sentry project could lift one and take over that cart.
export const SENSITIVE_QUERY_PARAMS = [
  'guest_session_token',
  'guest_token',
  // The LEGACY spelling. Readers accept it as a fallback
  // (`getUrlParameter("guest_session_token") || getUrlParameter("guestToken")`),
  // so a URL carrying only this one is still a live session — and it was not
  // being scrubbed anywhere, Sentry included. `guest_token` does not match it:
  // underscore vs camelCase, and the bare `token` entry cannot match either,
  // since the regex anchors each param to a ?, & or # boundary.
  'guestToken',
  'access_token',
  'refresh_token',
  'token',
  // The exchanged credit token from the claim redirect. It lives two hours
  // (EXCHANGED_CREDIT_TOKEN_TTL_SECONDS on the backend) and it authorises
  // SPENDING a customer's credit balance, so for that window it is a bearer
  // credential like any other. It is stripped from the
  // address bar on arrival (src/utils/credit-token.ts), but the pageview that
  // analytics reports fires on that same navigation — before the cleanup — so
  // the scrub has to happen here too, not only there.
  'credit_token',
];

/**
 * Replace the value of any sensitive query param with `[redacted]`.
 *
 * Deliberately regex-based rather than URL-parse based: this also has to work on
 * relative URLs, bare query strings, and console log lines that merely *contain*
 * a URL, none of which `new URL()` accepts.
 */
export function redactUrl(url: string): string {
  if (!url || (url.indexOf('=') === -1 && !/%3D/i.test(url))) return url;
  let out = url;
  for (const param of SENSITIVE_QUERY_PARAMS) {
    out = out.replace(
      new RegExp(`((?:^|[?&#])${param}=)[^&#\\s]*`, 'gi'),
      '$1[redacted]',
    );
    // The same URL percent-encoded inside another one: the Android in-app
    // browser hand-off (`intent://…;S.browser_fallback_url=https%3A…%26
    // guest_session_token%3D<jwt>`, in-app-browser.ts) carries the token in
    // this form, which the pattern above cannot see.
    out = out.replace(
      new RegExp(`((?:%3F|%26|%23)${param}%3D)[^%&#;\\s]*`, 'gi'),
      '$1[redacted]',
    );
  }
  return out;
}


interface SentryRequestData {
  request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
  breadcrumbs?: Array<{ message?: string; data?: Record<string, unknown> }>;
}

/** Shared client/server scrubber, including the Referer copy of checkout URLs. */
export function redactSentryEvent<T extends SentryRequestData>(event: T): T {
  const sensitive = (key: string) => SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase());
  const request = event.request;
  if (request) {
    if (request.url) request.url = redactUrl(request.url);
    const query = request.query_string;
    if (typeof query === 'string') request.query_string = redactUrl(query);
    else if (Array.isArray(query)) request.query_string = query.map(entry =>
      Array.isArray(entry) && typeof entry[0] === 'string' && sensitive(entry[0]) ? [entry[0], '[redacted]'] : entry);
    else if (query && typeof query === 'object') request.query_string = Object.fromEntries(
      Object.entries(query).map(([key, value]) => [key, sensitive(key) ? '[redacted]' : value]));
    for (const [key, value] of Object.entries(request.headers ?? {})) {
      request.headers![key] = /^(authorization|cookie|set-cookie|x-guest-token)$/i.test(key) ? '[redacted]' : redactUrl(value);
    }
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.message) breadcrumb.message = redactUrl(breadcrumb.message);
    for (const [key, value] of Object.entries(breadcrumb.data ?? {})) {
      if (typeof value === 'string') breadcrumb.data![key] = redactUrl(value);
    }
  }
  return event;
}
